import { Box, Column, ColumnProps, Row, Text } from "@hightouch/htui";
import { TooltipValue } from "./types";
import { ReactNode } from "react";

/** Utility functions for the ChartTooltip */

const getPrimaryLabel = (value: TooltipValue) => {
  if (typeof value === "string" || typeof value === "number") {
    return value;
  }

  return value.value;
};

const getSecondaryLabel = (value: TooltipValue) => {
  if (typeof value !== "string" && typeof value !== "number") {
    return value.description ?? null;
  }

  return null;
};

/** Shared components for the ChartTooltip */

/**
 * Wrapper to mirror Hightouch UI tooltip styles.
 */
export const ChartTooltipWrapper = ({ children, ...props }: ColumnProps) => {
  return (
    <Column
      bg="base.containerBackground"
      px={3}
      py={2}
      backdropFilter="blur(8px)"
      boxShadow="darkPopover"
      borderRadius="lg"
      gap={2}
      position="relative"
      {...props}
    >
      {children}
    </Column>
  );
};

export const ChartTooltipColor = ({
  color,
  width = 1,
}: {
  color: string;
  width?: number;
}) => {
  return <Box width={width} borderRadius="md" bg={color} flexShrink={0} />;
};

export const ChartTooltipTitle = ({ children }: { children: ReactNode }) => {
  return (
    <Text color="white" fontWeight="semibold" wordBreak="break-word">
      {children}
    </Text>
  );
};

export const ChartTooltipSubtitle = ({ children }: { children: ReactNode }) => {
  return (
    <Text color="whiteAlpha.80" size="sm" wordBreak="break-word">
      {children}
    </Text>
  );
};

export const ChartTooltipValue = ({ value }: { value: TooltipValue }) => {
  const primaryLabel = getPrimaryLabel(value);
  const secondaryLabel = getSecondaryLabel(value);

  return (
    <Box
      key={primaryLabel}
      bg="whiteAlpha.12"
      borderRadius="md"
      px={1}
      py={0.5}
      width="fit-content"
      textStyle="mono"
    >
      <Text color="white" fontWeight="medium" size="sm">
        {primaryLabel}
      </Text>
      {secondaryLabel && (
        <Text color="whiteAlpha.60" fontWeight="medium" size="sm">
          {" "}
          {secondaryLabel}
        </Text>
      )}
    </Box>
  );
};

export const ChartTooltipValueDescription = ({
  children,
}: {
  children: ReactNode;
}) => {
  return (
    <Row maxWidth="300px">
      <ChartTooltipSubtitle>{children}</ChartTooltipSubtitle>
    </Row>
  );
};
